import { db } from '../firebase';
import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  setDoc
} from 'firebase/firestore';

/**
 * Helper to validate content data based on the section type.
 *
 * For new content (isUpdate === false) the following is required:
 * - podcasts: must have a "text" field that is an array.
 * - lectures/books: must have "author" and "voice" fields. May have "text" (array) for main content.
 *   "author" (string) and "voice" (string) fields.
 *
 * For updates (isUpdate === true) the function can be more lenient.
 */
const validateContentData = (section, contentData, isUpdate = false) => {
  if (section === 'podcasts') {
    if (!isUpdate && (!contentData.text || !Array.isArray(contentData.text))) {
      throw new Error("Podcasts content must have a 'text' field as an array of paragraphs.");
    }
    } else if (section === 'lectures' || section === 'books') {
    if (!isUpdate) {
      // Required fields for lectures/books
      if (!contentData.author) {
        throw new Error(`${section} content must have an 'author' field.`);
      }
      if (!contentData.voice) {
        throw new Error(`${section} content must have a 'voice' field.`);
      }

      // Optional: text field (for content that has text in main document vs subcollection chapters)
      if (contentData.text && !Array.isArray(contentData.text)) {
        throw new Error(`${section} content 'text' field must be an array of paragraphs.`);
      }

      // Note: Chapters are now stored in subcollections, so no validation needed here
    }
  } else {
    throw new Error("Unsupported section type");
  }
};

/**
 * Fetches content for a given section and language.
 * Firestore path: contentSection/{language}/{section}
 *
 * @param {string} section - The content section ('podcasts', 'lectures', or 'books')
 * @param {string} language - The language code (e.g., 'en', 'es', etc.)
 * @returns {Array} Array of content documents.
 */
export const getContentExtra = async (section, language) => {
  try {
    const contentCollection = collection(db, 'contentSection', language, section);
    const contentSnapshot = await getDocs(contentCollection);
    const content = contentSnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
    return content;
  } catch (error) {
    console.error(`Error fetching ${section} content for language ${language}:`, error);
    return [];
  }
};

/**
 * Creates a new content document in the specified section and language.
 *
 * @param {string} section - The content section.
 * @param {string} language - The language code.
 * @param {object} contentData - The content data (must follow the structure for that section).
 * @param {string|null} customId - Optional custom ID to use instead of auto-generated one.
 * @returns {object} The newly created document data with its ID.
 */
export const createContentExtra = async (section, language, contentData, customId = null) => {
  try {
    // Validate content data based on its structure.
    validateContentData(section, contentData);

    if (customId) {
      // Use setDoc with custom ID when provided (for cloning to preserve source ID)
      const docRef = doc(db, 'contentSection', language, section, customId);
      await setDoc(docRef, contentData);
      console.log(`✅ Created content with custom ID: ${customId}`);
      return { id: customId, ...contentData };
    } else {
      // Use addDoc for auto-generated ID (for new content)
      const contentCollection = collection(db, 'contentSection', language, section);
      const docRef = await addDoc(contentCollection, contentData);
      console.log(`✅ Created content with auto-generated ID: ${docRef.id}`);
      return { id: docRef.id, ...contentData };
    }
  } catch (error) {
    console.error(`Error creating ${section} content for language ${language}:`, error);
    throw error;
  }
};

/**
 * Updates an existing content document.
 *
 * @param {string} section - The content section.
 * @param {string} language - The language code.
 * @param {string} contentId - The Firestore document ID.
 * @param {object} updateData - The data to update (may be partial).
 * @returns {boolean} True if the update was successful.
 */
export const updateContentExtra = async (section, language, contentId, updateData) => {
  try {
    // Optionally validate the updateData; for updates you might only update a subset of fields.
    validateContentData(section, updateData, true);
    const contentDoc = doc(db, 'contentSection', language, section, contentId);
    await updateDoc(contentDoc, updateData);
    return true;
  } catch (error) {
    console.error(`Error updating ${section} content ${contentId} for language ${language}:`, error);
    throw error;
  }
};

/**
 * Deletes a content document.
 *
 * @param {string} section - The content section.
 * @param {string} language - The language code.
 * @param {string} contentId - The Firestore document ID.
 * @returns {boolean} True if the deletion was successful.
 */
export const removeContentExtra = async (section, language, contentId) => {
  try {
    const contentDoc = doc(db, 'contentSection', language, section, contentId);
    await deleteDoc(contentDoc);
    return true;
  } catch (error) {
    console.error(`Error deleting ${section} content ${contentId} for language ${language}:`, error);
    throw error;
  }
};

// For convenience, you can export createContentExtra as addContentExtra.
export const addContentExtra = createContentExtra;

/**
 * Process and generate audio for content chapters using ElevenLabs
 * This function takes a content item and generates audio for all its chapters
 *
 * @param {string} section - The content section ('lectures' or 'books')
 * @param {string} language - The language code
 * @param {string} contentId - The content document ID
 * @param {object} contentData - The content data with chapters
 * @param {string} voiceId - The ElevenLabs voice ID (e.g., 'H42MeRDunc17uM624Bpd' for Neville Goddard)
 * @returns {object} Updated content data with audio URLs
 */
export const processContentChaptersAudio = async (section, language, contentId, contentData, voiceId) => {
  try {
    const { ttsGenerate } = await import('./content/googleFunctions');

    if (!contentData.chapters || !Array.isArray(contentData.chapters)) {
      throw new Error('Content must have chapters array');
    }

    const updatedChapters = [];

    // Process each chapter
    for (let i = 0; i < contentData.chapters.length; i++) {
      const chapter = contentData.chapters[i];
      const chapterIndex = i + 1;

      try {
        // Prepare the text for TTS (join paragraphs with proper spacing)
        const chapterText = Array.isArray(chapter.text)
          ? chapter.text.join(' ')
          : chapter.text;

        // Generate audio for this chapter
        const response = await ttsGenerate({
          inputText: [chapterText],
          storeAudioPath: `contentSection/${language}/${section}/${contentData.title}/${contentData.author}_${contentData.title}_${chapterIndex}.mp3`,
          storeAudioURLPath: `contentSection/${language}/${section}/${contentId}/chapters/${chapterIndex}`,
          voiceName: voiceId,
          type: 'contentExtra',
          language: language,
          audioStyle: 'read',
          voiceType: 'elevenLabs'
        });

        const { fileUrl: audioURL, duration: audioDuration } = response.data;

        // Update chapter with audio info
        const updatedChapter = {
          ...chapter,
          audioURL,
          audioDuration: audioDuration || 0,
          id: chapterIndex,
          title: chapter.title || `Chapter ${chapterIndex}`
        };

        updatedChapters.push(updatedChapter);

        console.log(`Audio generated for chapter ${chapterIndex}: ${audioURL}`);

        // Add a small delay to avoid rate limiting
        await new Promise(resolve => setTimeout(resolve, 1000));

      } catch (chapterError) {
        console.error(`Error generating audio for chapter ${chapterIndex}:`, chapterError);
        // Keep the chapter without audio
        updatedChapters.push({
          ...chapter,
          id: chapterIndex,
          title: chapter.title || `Chapter ${chapterIndex}`,
          audioURL: '',
          audioDuration: 0
        });
      }
    }

    // Update the content with the processed chapters
    const updatedContentData = {
      ...contentData,
      chapters: updatedChapters
    };

    // Save the updated content to Firestore
    await updateContentExtra(section, language, contentId, updatedContentData);

    return updatedContentData;

  } catch (error) {
    console.error('Error processing content chapters audio:', error);
    throw error;
  }
};

/**
 * Add a new chapter to existing content
 *
 * @param {string} section - The content section
 * @param {string} language - The language code
 * @param {string} contentId - The content document ID
 * @param {object} chapterData - The new chapter data
 * @param {string} voiceId - The ElevenLabs voice ID
 * @returns {boolean} Success status
 */
export const addChapterToContent = async (section, language, contentId, chapterData, voiceId) => {
  try {
    const { ttsGenerate } = await import('./content/googleFunctions');

    // Get current content
    const currentContent = await getContentExtra(section, language);
    const contentItem = currentContent.find(item => item.id === contentId);

    if (!contentItem) {
      throw new Error('Content item not found');
    }

    const currentChapters = contentItem.chapters || [];
    const newChapterId = currentChapters.length + 1;

    // Prepare chapter text
    const chapterText = Array.isArray(chapterData.text)
      ? chapterData.text.join(' ')
      : chapterData.text.split('\n').filter(p => p.trim());

    let audioURL = '';
    let audioDuration = 0;

    // Generate audio if voice is provided
    if (voiceId && chapterText) {
      try {
        const response = await ttsGenerate({
          inputText: [chapterText],
          storeAudioPath: `contentSection/${language}/${section}/${contentItem.title}/${contentItem.author}_${contentItem.title}_${newChapterId}.mp3`,
          storeAudioURLPath: `contentSection/${language}/${section}/${contentId}/chapters/${newChapterId}`,
          voiceName: voiceId,
          type: 'contentExtra',
          language: language,
          audioStyle: 'read',
          voiceType: 'elevenLabs'
        });

        // Cloud function returns different formats in different situations
        audioURL = response.fileUrl || response.data?.fileUrl;
        audioDuration = response.duration || response.data?.duration || 0;

      } catch (audioError) {
        console.error('Error generating audio for new chapter:', audioError);
        // Continue without audio
      }
    }

    // Create new chapter
    const newChapter = {
      id: newChapterId,
      title: chapterData.title || `Chapter ${newChapterId}`,
      text: Array.isArray(chapterData.text) ? chapterData.text : chapterData.text.split('\n').filter(p => p.trim()),
      audioURL,
      audioDuration
    };

    // Update content with new chapter
    const updatedChapters = [...currentChapters, newChapter];
    await updateContentExtra(section, language, contentId, { chapters: updatedChapters });

    return true;

  } catch (error) {
    console.error('Error adding chapter to content:', error);
    throw error;
  }
};

/**
 * Fetch chapters from subcollection for a content item
 * Firestore path: contentSection/{language}/{section}/{contentId}/chapters
 *
 * @param {string} section - The content section
 * @param {string} language - The language code
 * @param {string} contentId - The content document ID
 * @returns {Array} Array of chapter documents
 */
export const getContentChapters = async (section, language, contentId) => {
  try {
    const chaptersCollection = collection(db, 'contentSection', language, section, contentId.toString(), 'chapters');

    const chaptersSnapshot = await getDocs(chaptersCollection);
        const chapters = chaptersSnapshot.docs
      .filter(docSnapshot => {
        // Filter out chunk documents (they contain '_chunk_' in their doc ID)
        return !docSnapshot.id.includes('_chunk_');
      })
      .map((docSnapshot) => {
        const data = docSnapshot.data();
        const chapter = {
          docId: docSnapshot.id, // Keep Firestore doc ID separate
          id: data.id || parseInt(docSnapshot.id), // Use data.id if exists, fallback to parsed doc.id
          ...data
        };

      // DEBUGGING: Log each chapter's text field
      console.log(`🔍 Retrieved chapter ${chapter.id}:`, {
        title: chapter.title,
        text: chapter.text,
        textIsArray: Array.isArray(chapter.text),
        textLength: chapter.text?.length,
        audioURL: chapter.audioURL ? 'Present' : 'Missing',
        hasError: !!(chapter.audioError || chapter.audioErrorMessage)
      });

      return chapter;
    });

    // Handle error clearing for chapters with audio but still showing errors
    const chaptersToUpdate = chapters.filter(chapter =>
      chapter.audioURL && (chapter.audioError || chapter.audioErrorMessage)
    );

    if (chaptersToUpdate.length > 0) {
      console.log(`🧹 Found ${chaptersToUpdate.length} chapters with audio but error flags - clearing...`);

      // Clear errors for these chapters (don't await to avoid blocking)
      chaptersToUpdate.forEach(chapter => {
        console.log(`🧹 Chapter ${chapter.id}: Audio exists, clearing error flags (${chapter.audioErrorMessage})`);

        const chapterDocRef = doc(db, 'contentSection', language, section, contentId.toString(), 'chapters', chapter.docId);
        setDoc(chapterDocRef, {
          audioError: false,
          audioErrorMessage: '',
          audioGenerating: false,
          timeoutOccurred: false
        }, { merge: true }).then(() => {
          console.log(`✅ Cleared error flags for chapter ${chapter.id}`);
        }).catch(error => {
          console.error(`❌ Error clearing error flags for chapter ${chapter.id}:`, error);
        });

        // Update the chapter object for immediate UI
        chapter.audioError = false;
        chapter.audioErrorMessage = '';
        chapter.audioGenerating = false;
        chapter.timeoutOccurred = false;
      });
    }

    // Sort by numeric ID
    chapters.sort((a, b) => {
      const aId = typeof a.id === 'number' ? a.id : parseInt(a.id);
      const bId = typeof b.id === 'number' ? b.id : parseInt(b.id);
      return aId - bId;
    });

    console.log(`📖 Fetched ${chapters.length} chapters for ${section}/${contentId}:`, chapters.map(c => `${c.id}: ${c.title}`));
    return chapters;
  } catch (error) {
    console.error(`Error fetching chapters for ${section}/${contentId}:`, error);
    return [];
  }
};

/**
 * Add a chapter to a content item's subcollection
 * Firestore path: contentSection/{language}/{section}/{contentId}/chapters/{chapterId}
 *
 * @param {string} section - The content section
 * @param {string} language - The language code
 * @param {string} contentId - The content document ID
 * @param {object} chapterData - The chapter data
 * @param {string|null} voiceId - Optional voice ID for audio generation
 * @returns {object} The created chapter with its ID
 */
export const addChapterToSubcollection = async (section, language, contentId, chapterData, voiceId = null) => {
  try {
    // Get content item to access voice and other metadata
    const currentContent = await getContentExtra(section, language);
    const contentItem = currentContent.find(item => item.id === contentId);

    if (!contentItem) {
      throw new Error('Content item not found');
    }

    // Get current chapters to determine next ID
    const currentChapters = await getContentChapters(section, language, contentId);
    const newChapterId = currentChapters.length + 1;

    // Validate IDs and path components
    console.log('🔍 Validating path components:');
    console.log('  section:', section, typeof section);
    console.log('  language:', language, typeof language);
    console.log('  contentId:', contentId, typeof contentId);
    console.log('  newChapterId:', newChapterId, typeof newChapterId);

    if (!section || !language || !contentId) {
      throw new Error(`Invalid path components: section=${section}, language=${language}, contentId=${contentId}`);
    }

    // Prepare chapter text - split by lines and filter empty ones
    let chapterTextArray = Array.isArray(chapterData.text)
      ? chapterData.text
      : chapterData.text.split('\n').filter(p => p.trim() !== '');

    // Sanitize text for Firestore - clean any problematic characters
    chapterTextArray = chapterTextArray.map(text => {
      if (typeof text !== 'string') {
        console.warn('⚠️ Non-string text found:', text);
        return String(text);
      }

      // Clean problematic characters that might break Firestore
      const cleanedText = text
        .replace(/[\u0000-\u001F\u007F-\u009F]/g, '') // Remove control characters
        .replace(/\u00A0/g, ' ') // Replace non-breaking spaces with regular spaces
        .trim();

      // Firestore has a limit of ~1MB per field, but let's be safe with paragraphs
      if (cleanedText.length > 50000) {
        console.warn(`⚠️ Text paragraph very long (${cleanedText.length} chars), truncating...`);
        return cleanedText.substring(0, 50000) + '...';
      }

      return cleanedText;
    }).filter(text => text.length > 0); // Remove any empty strings after cleaning

    console.log('🔍 DEBUGGING BACKEND - Chapter text processing:');
    console.log('📨 Received chapterData.text:', chapterData.text);
    console.log('🔍 Is array?', Array.isArray(chapterData.text));
    console.log('📊 Final chapterTextArray:', chapterTextArray);
    console.log('🧹 Text sanitized and cleaned for Firestore');

    // Create chapter document immediately (without audio)
    // FREEMIUM MODEL: First chapter is always FREE, others require PRO
    const accessLevel = newChapterId === 1 ? 'FREE' : 'PRO';

    const newChapter = {
      id: newChapterId,
      title: chapterData.title || `Chapter ${newChapterId}`,
      text: chapterTextArray,
      audioURL: chapterData.audioURL || '',
      audioDuration: 0,
      audioGenerating: false, // Will be set to true when generation starts
      accessLevel: accessLevel, // FREE for chapter 1, PRO for others
      createdAt: new Date().toISOString()
    };

    console.log('💾 Storing chapter immediately:');
    console.log('  newChapter:', newChapter);
    console.log('  text field:', newChapter.text);
    console.log('  text is array?', Array.isArray(newChapter.text));
    console.log('  text length:', newChapter.text.length);

    // CRITICAL: Convert contentId to string - Firestore paths require strings
    console.log('🚨 PRE-DOC PATH DEBUG:');
    console.log('  contentId raw:', contentId, typeof contentId);
    console.log('  language:', language, typeof language);
    console.log('  section:', section, typeof section);
    console.log('  newChapterId:', newChapterId, typeof newChapterId);

    const contentIdString = String(contentId);
    const newChapterIdString = String(newChapterId);

    console.log('  contentId converted:', contentIdString, typeof contentIdString);
    console.log('  newChapterId converted:', newChapterIdString, typeof newChapterIdString);

    // Define chapterDocRef outside try block so it's accessible for audio generation
    const chapterDocRef = doc(db, 'contentSection', language, section, contentIdString, 'chapters', newChapterIdString);

    // Validate chapter data before storing
    try {
      // Ensure all text elements are valid strings
      newChapter.text.forEach((text, index) => {
        if (typeof text !== 'string') {
          throw new Error(`Text at index ${index} is not a string: ${typeof text}`);
        }
        if (text.length === 0) {
          throw new Error(`Text at index ${index} is empty`);
        }
      });

      console.log('🔍 Attempting to store chapter with path:', `contentSection/${language}/${section}/${contentIdString}/chapters/${newChapterIdString}`);

      await setDoc(chapterDocRef, newChapter);

      console.log(`✅ Chapter ${newChapterIdString} stored immediately in ${section}/${contentIdString}`);

    } catch (storeError) {
      console.error('❌ Error storing chapter to Firestore:', storeError);
      console.error('📦 Chapter data that failed to store:', newChapter);
      console.error('📝 Text data that failed:', newChapter.text);
      throw new Error(`Failed to store chapter: ${storeError.message}`);
    }

    // Generate audio if needed (in background)
    const shouldGenerateAudio = !chapterData.audioURL && chapterTextArray.length > 0;
    // Always use NEVILLE_GODDARD_VOICE if no specific voice provided
    const effectiveVoiceId = voiceId || contentItem.voice || 'H42MeRDunc17uM624Bpd';

    if (shouldGenerateAudio) {
      console.log('🎵 Starting background audio generation...');
      console.log('🎙️ Voice ID to use:', effectiveVoiceId);

      // Update chapter to show audio is generating
      await setDoc(chapterDocRef, { ...newChapter, audioGenerating: true }, { merge: true });

      // Generate audio in background (don't await)
      generateChapterAudioInBackground(
        section,
        language,
        contentIdString, // Use string version
        newChapterIdString, // Use string version
        chapterTextArray,
        effectiveVoiceId,
        contentItem
      ).catch(error => {
        console.error('Background audio generation failed:', error);

        // Only mark as error if it's not a timeout
        if (error.message !== 'TTS_TIMEOUT') {
          setDoc(chapterDocRef, {
            audioGenerating: false,
            audioError: true,
            audioErrorMessage: error.message
          }, { merge: true });
        }
        // If it's a timeout, let the timeout handling in generateChapterAudioInBackground manage the state
      });
    } else {
      console.log('🚫 Audio generation skipped - no text content or audio already exists');
    }

    return { docId: newChapterId.toString(), ...newChapter };

  } catch (error) {
    console.error('Error adding chapter to subcollection:', error);
    throw error;
  }
};

/**
 * Get a single content item by ID
 */
const getContentExtraItem = async (section, language, contentId) => {
  try {
    const docRef = doc(db, 'contentSection', language, section, String(contentId));
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      return { id: docSnap.id, ...docSnap.data() };
    }
    return null;
  } catch (error) {
    console.error('Error getting content item:', error);
    return null;
  }
};

/**
 * Generate audio for an existing chapter that doesn't have audio
 * This is for manual audio generation (e.g., when user clicks "Generate Audio" button)
 */
export const generateAudioForChapter = async (section, language, contentId, chapterId, voiceId = 'H42MeRDunc17uM624Bpd') => {
  try {
    console.log(`🎙️ Manual audio generation requested for chapter ${chapterId}...`);

    // Get the chapter data
    const chapterDocRef = doc(db, 'contentSection', language, section, String(contentId), 'chapters', String(chapterId));
    const chapterDoc = await getDoc(chapterDocRef);

    if (!chapterDoc.exists()) {
      throw new Error(`Chapter ${chapterId} not found`);
    }

    const chapterData = chapterDoc.data();
    const chapterText = chapterData.text;

    if (!chapterText || chapterText.length === 0) {
      throw new Error('Chapter has no text content for audio generation');
    }

    // Get content item for metadata
    const contentItem = await getContentExtraItem(section, language, contentId);
    if (!contentItem) {
      throw new Error('Content item not found');
    }

    // Update chapter to show audio is generating
    await setDoc(chapterDocRef, { audioGenerating: true, audioError: false }, { merge: true });

    console.log('🎙️ Starting audio generation for existing chapter:', {
      chapterId,
      textLength: Array.isArray(chapterText) ? chapterText.length : 1,
      voiceId
    });

    // Use the background audio generation function (don't await to allow timeout handling)
    generateChapterAudioInBackground(
      section,
      language,
      String(contentId),
      String(chapterId),
      Array.isArray(chapterText) ? chapterText : [chapterText],
      voiceId,
      contentItem
    ).catch(error => {
      console.error(`❌ Error generating audio for chapter ${chapterId}:`, error);

      // Only mark as error if it's not a timeout
      if (error.message !== 'TTS_TIMEOUT') {
        const chapterDocRef = doc(db, 'contentSection', language, section, String(contentId), 'chapters', String(chapterId));
        setDoc(chapterDocRef, {
          audioGenerating: false,
          audioError: true,
          audioErrorMessage: error.message
        }, { merge: true });
      }
      // If it's a timeout, let the timeout handling in generateChapterAudioInBackground manage the state
    });

    console.log(`🎵 Manual audio generation started for chapter ${chapterId} (processing in background)...`);

  } catch (error) {
    console.error(`❌ Error starting audio generation for chapter ${chapterId}:`, error);

    // This catch is for errors in setting up the generation, not TTS timeouts
    const chapterDocRef = doc(db, 'contentSection', language, section, String(contentId), 'chapters', String(chapterId));
    await setDoc(chapterDocRef, {
      audioGenerating: false,
      audioError: true,
      audioErrorMessage: error.message
    }, { merge: true });

    throw error;
  }
};

// Background audio generation function with robust timeout handling
// Chunking functions moved to src/helpers/chunkingLogic.js (experimental, not used in production)

const generateChapterAudioInBackground = async (
  section,
  language,
  contentId,
  chapterId,
  chapterTextArray,
  voiceId,
  contentItem
) => {
  const chapterDocRef = doc(db, 'contentSection', language, section, String(contentId), 'chapters', String(chapterId));

  try {
    console.log(`🎙️ SIMPLE TTS generation for chapter ${chapterId}...`);

    const { ttsGenerate } = await import('./content/googleFunctions');

    // Join paragraphs into full text - SIMPLE APPROACH
    const fullText = chapterTextArray.join(' ');
    console.log(`📚 Full text length: ${fullText.length} characters`);

    // SIMPLE: Send entire text as one chunk to ElevenLabs
    console.log(`🎵 Generating single audio for entire chapter...`);

    console.log(`🔧 SIMPLE: Using storage paths:`);
    console.log(`  📁 Audio file: contentSection/${language}/${section}/${contentId}/chapter_${chapterId}.mp3`);
    console.log(`  🔗 URL path: contentSection/${language}/${section}/${contentId}/chapters/${chapterId}`);

    // Add timeout wrapper for long ElevenLabs processing (up to 5 minutes)
    const ttsWithTimeout = async () => {
      return Promise.race([
        ttsGenerate({
          inputText: chapterTextArray, // Send original array format
          storeAudioPath: `contentSection/${language}/${section}/${contentId}/chapter_${chapterId}.mp3`,
          storeAudioURLPath: `contentSection/${language}/${section}/${contentId}/chapters/${chapterId}`,
          voiceName: voiceId,
          type: 'contentExtra',
          language: language,
          audioStyle: 'read',
          voiceType: 'elevenLabs'
        }),
        new Promise((_, reject) =>
          setTimeout(() => reject(new Error('TTS_TIMEOUT')), 300000) // 5 minutes
        )
      ]);
    };

        let response, audioURL, audioDuration;

    try {
      response = await ttsWithTimeout();
      console.log('🔍 SIMPLE: Raw response from ttsGenerate:', response);
      console.log('🔍 SIMPLE: Response type:', typeof response);
      console.log('🔍 SIMPLE: Response keys:', Object.keys(response || {}));
    } catch (firebaseError) {
      console.log('🔍 SIMPLE: Firebase error caught:', firebaseError.message);

      // Check if this is the "Response is missing data field" error
      if (firebaseError.message && firebaseError.message.includes('Response is missing data field')) {
        console.log('🔍 SIMPLE: Known Firebase internal error - audio likely generated successfully');

        // Check if error contains response data
        const possibleResponse = firebaseError.details || firebaseError.data || firebaseError.response || firebaseError.result;

        if (possibleResponse) {
          console.log('🔍 SIMPLE: Found response data in error:', possibleResponse);
          response = possibleResponse;
        } else {
          console.log('🔍 SIMPLE: No response data in error - audio likely generated but response lost');
          console.log('🔍 SIMPLE: Manual check needed in Firebase Storage at:', `contentSection/${language}/${section}/${contentId}/chapter_${chapterId}.mp3`);

          // Try to create a generic response pointing to expected location
          // This won't have duration, but at least provides the expected file path
          const { firebaseConfig } = await import('../firebase');
          const projectId = firebaseConfig.projectId;
          const expectedPath = `contentSection%2F${language}%2F${section}%2F${contentId}%2Fchapter_${chapterId}.mp3`;

          response = {
            fileUrl: `https://firebasestorage.googleapis.com/v0/b/${projectId}.appspot.com/o/${expectedPath}?alt=media`,
            duration: 0, // Unknown duration due to error
            note: 'Generated from expected path due to Firebase error'
          };

          console.log('🔍 SIMPLE: Created expected response:', response);
        }
      } else {
        // This is a real error
        throw firebaseError;
      }
    }

    // Try multiple possible response formats (learned from chunking experiments)
    audioURL = null;
    audioDuration = 0;

    if (response) {
      audioURL = response.fileUrl || response.data?.fileUrl || response.url || response.audioUrl;
      audioDuration = response.duration || response.data?.duration || response.audioDuration || 0;

      console.log('🔍 SIMPLE: Extracted audioURL:', audioURL);
      console.log('🔍 SIMPLE: Extracted duration:', audioDuration);
    }

    if (!audioURL) {
      console.error('❌ SIMPLE: No audio URL found in response (likely Firebase internal error)');
      console.log('🔍 SIMPLE: Audio may have been generated but response lost due to Firebase error');
      console.log('🔍 SIMPLE: CHECK MANUALLY: Go to Firebase Storage and look for:', `contentSection/${language}/${section}/${contentId}/chapter_${chapterId}.mp3`);

      // Get expected paths for user reference
      const { firebaseConfig } = await import('../firebase');
      const projectId = firebaseConfig.projectId;
      const expectedStoragePath = `contentSection/${language}/${section}/${contentId}/chapter_${chapterId}.mp3`;
      const consoleURL = `https://console.firebase.google.com/u/0/project/${projectId}/storage/${projectId}.appspot.com/files/~2FcontentSection%2F${language}%2F${section}%2F${contentId}`;

      console.log('\n🔧 MANUAL ACTION REQUIRED:');
      console.log('📍 1. Check Firebase Storage Console:', consoleURL);
      console.log('📍 2. Look for file:', expectedStoragePath);
      console.log('📍 3. If file exists, copy its download URL');
      console.log('📍 4. Use manuallyUpdateChapterAudio() function to update the record');
      console.log('📍 5. Example: manuallyUpdateChapterAudio("lectures", "en", "1", "1", "YOUR_AUDIO_URL")\n');

      // Mark as potential success but requiring manual verification
      await setDoc(chapterDocRef, {
        audioGenerating: false,
        audioError: false, // Don't mark as error since audio might exist
        audioURL: '', // Empty for now
        audioDuration: 0,
        processingStatus: `⚠️ MANUAL CHECK NEEDED - Audio likely generated`,
        needsManualVerification: true,
        expectedStoragePath,
        consoleURL,
        errorNote: 'ElevenLabs likely generated audio but Firebase response failed',
        manualInstructions: 'Check Firebase Storage console and use manuallyUpdateChapterAudio() if audio exists'
      }, { merge: true });

      return; // Don't throw error, just return - let user check manually
    }

    await setDoc(chapterDocRef, {
      audioURL,
      audioDuration,
      audioGenerating: false,
      audioError: false,
      timeoutOccurred: false,
      audioGeneratedAt: new Date().toISOString(),
      processingMethod: 'simple-single-audio',
      originalTextLength: fullText.length,
      textLength: fullText.length,
      chunkCount: 1
    }, { merge: true });

    console.log(`✅ SIMPLE audio generated for chapter ${chapterId}: ${audioURL} (${audioDuration}s)`);

  } catch (error) {
    console.error(`❌ SIMPLE audio generation failed for chapter ${chapterId}:`, error);

    // Handle timeout specifically
    if (error.message === 'TTS_TIMEOUT') {
      console.warn(`⏰ TTS generation timed out for chapter ${chapterId}, but may still be processing...`);

      await setDoc(chapterDocRef, {
        audioGenerating: true,
        audioError: false,
        timeoutOccurred: true,
        lastTimeoutAt: new Date().toISOString(),
        processingStatus: 'Timed out but may still be processing in background...'
      }, { merge: true });

      startBackgroundAudioPolling(section, language, contentId, chapterId, contentItem);
    } else {
      // Real error
      await setDoc(chapterDocRef, {
        audioGenerating: false,
        audioError: true,
        audioErrorMessage: error.message,
        timeoutOccurred: false,
        processingStatus: `Error: ${error.message}`
      }, { merge: true });
    }

    throw error;
  }
};

// Simplified background monitoring - just keep audioGenerating true and let frontend polling handle detection
const startBackgroundAudioPolling = async (section, language, contentId, chapterId, contentItem) => {
  console.log(`🔄 TTS timed out for chapter ${chapterId}, but keeping audioGenerating=true for frontend polling to handle...`);

  // The TTS might still complete on the server side and update Firestore
  // The frontend polling (every 3 seconds) will detect when audioGenerating becomes false
  // and audioURL appears, even if it takes 5-10 minutes

  // Set a final timeout after 15 minutes - if still no audio, mark as error
  setTimeout(async () => {
    try {
      const chapterDocRef = doc(db, 'contentSection', language, section, String(contentId), 'chapters', String(chapterId));
      const chapterDoc = await getDoc(chapterDocRef);

      if (chapterDoc.exists()) {
        const chapterData = chapterDoc.data();

        // Only mark as error if still generating and no audio after 15 minutes
        if (chapterData.audioGenerating && !chapterData.audioURL) {
          console.warn(`⏰ Final timeout reached for chapter ${chapterId} - marking as error`);

          await setDoc(chapterDocRef, {
            audioGenerating: false,
            audioError: true,
            audioErrorMessage: 'TTS generation timed out after 15 minutes',
            timeoutOccurred: true
          }, { merge: true });
        }
      }
    } catch (error) {
      console.error(`❌ Error in final timeout check for chapter ${chapterId}:`, error);
    }
  }, 900000); // 15 minutes
};

/**
 * Fetch content with chapters from subcollections
 * This combines the main content document with its chapters subcollection
 *
 * @param {string} section - The content section
 * @param {string} language - The language code
 * @returns {Array} Array of content documents with chapters
 */
export const getContentExtraWithChapters = async (section, language) => {
  try {
    console.log(`🚀 Fetching content for ${section}/${language}...`);
    const content = await getContentExtra(section, language);
    console.log(`📚 Found ${content.length} content items for ${section}/${language}`);

    // For lectures and books, fetch chapters from subcollections
    if (section === 'lectures' || section === 'books') {
      const contentWithChapters = await Promise.all(
        content.map(async (item) => {
          console.log(`🔍 Processing content item: ${item.id} - "${item.title}"`, section, language, item.id, typeof item.id);
          const subcollectionChapters = await getContentChapters(section, language, item.id);
          console.log('subcollectionChapters', subcollectionChapters);

          // After migration, prioritize subcollection chapters
          // Only use document array chapters if no subcollection chapters exist
          let finalChapters = [];

          if (subcollectionChapters.length > 0) {
            // Use subcollection chapters (post-migration)
            finalChapters = subcollectionChapters;
            console.log(`📖 ✅ Using ${subcollectionChapters.length} chapters from subcollection for "${item.title}"`);
          } else if (item.chapters && Array.isArray(item.chapters) && item.chapters.length > 0) {
            // Fallback to document array chapters (pre-migration)
            finalChapters = item.chapters;
            console.log(`📚 ⚠️ Using ${item.chapters.length} chapters from document array for "${item.title}" - migration may be needed`);
          } else {
            // No chapters found anywhere
            finalChapters = [];
            console.log(`📭 ❌ No chapters found for "${item.title}" (ID: ${item.id})`);
            console.log(`   Subcollection chapters:`, subcollectionChapters);
            console.log(`   Document chapters:`, item.chapters);
          }

          // Sort by numeric ID (if not already sorted from subcollection)
          if (subcollectionChapters.length === 0 && finalChapters.length > 0) {
            finalChapters.sort((a, b) => {
              const aId = typeof a.id === 'number' ? a.id : parseInt(a.id);
              const bId = typeof b.id === 'number' ? b.id : parseInt(b.id);
              return aId - bId;
            });
          }

          const result = {
            ...item,
            chapters: finalChapters
          };

          console.log(`✅ Final result for "${item.title}": ${finalChapters.length} chapters`);
          console.log(`🔍 DEBUG: Chapters being returned:`, finalChapters.map(c => ({ id: c.id, title: c.title, hasAudio: !!c.audioURL })));
          return result;
        })
      );

      console.log(`🎯 Returning ${contentWithChapters.length} content items with chapters`);
      return contentWithChapters;
    }

    return content;
  } catch (error) {
    console.error(`❌ Error fetching ${section} content with chapters:`, error);
    return [];
  }
};

/**
 * Migrate chapters from document array to subcollection
 * This moves chapters from the main document to the chapters subcollection
 *
 * @param {string} section - The content section
 * @param {string} language - The language code
 * @param {string} contentId - The content document ID
 * @returns {boolean} Success status
 */
export const migrateChaptersToSubcollection = async (section, language, contentId) => {
  try {
    const currentContent = await getContentExtra(section, language);
    const contentItem = currentContent.find(item => item.id === contentId);

    if (!contentItem || !contentItem.chapters) {
      console.log(`No chapters to migrate for ${contentId}`);
      return false;
    }

    const documentsChapters = contentItem.chapters;
    console.log(`Migrating ${documentsChapters.length} chapters for ${contentId}`);

    // Create chapters in subcollection
    for (const chapter of documentsChapters) {
      const chapterDocRef = doc(db, 'contentSection', language, section, contentId, 'chapters', chapter.id.toString());

      // Set access level during migration (FREEMIUM MODEL)
      const accessLevel = chapter.id === 1 ? 'FREE' : 'PRO';

      await setDoc(chapterDocRef, {
        ...chapter,
        accessLevel: chapter.accessLevel || accessLevel, // Preserve existing or set based on chapter number
        migratedAt: new Date().toISOString(),
        migratedFrom: 'document_array'
      });
      console.log(`Migrated chapter ${chapter.id} to subcollection with access level: ${accessLevel}`);
    }

    // Remove chapters array from main document
    await updateContentExtra(section, language, contentId, {
      chapters: [],
      chaptersmigratedAt: new Date().toISOString()
    });

    console.log(`Successfully migrated chapters for ${contentId}`);
    return true;

  } catch (error) {
    console.error(`Error migrating chapters for ${contentId}:`, error);
    throw error;
  }
};

/**
 * Update a chapter in subcollection
 * Firestore path: contentSection/{language}/{section}/{contentId}/chapters/{chapterId}
 *
 * @param {string} section - The content section
 * @param {string} language - The language code
 * @param {string} contentId - The content document ID
 * @param {string} chapterId - The chapter ID
 * @param {object} updateData - The data to update
 * @returns {boolean} Success status
 */
export const updateChapterInSubcollection = async (section, language, contentId, chapterId, updateData) => {
  try {
    const chapterDocRef = doc(db, 'contentSection', language, section, contentId.toString(), 'chapters', chapterId.toString());

    // Preserve accessLevel when updating chapters
    // First chapter is always FREE, others are PRO
    const preservedAccessLevel = parseInt(chapterId) === 1 ? 'FREE' : 'PRO';

    const updatePayload = {
      ...updateData,
      accessLevel: updateData.accessLevel || preservedAccessLevel, // Preserve or set based on chapter number
      updatedAt: new Date().toISOString()
    };

    await updateDoc(chapterDocRef, updatePayload);

    console.log(`Chapter ${chapterId} updated in ${section}/${contentId} with access level: ${updatePayload.accessLevel}`);
    return true;

  } catch (error) {
    console.error('Error updating chapter in subcollection:', error);
    throw error;
  }
};

// Experimental chunking functions moved to src/helpers/chunkingLogic.js

/**
 * TEST FUNCTION: Generate audio for just a few words to debug issues
 */
export const testSimpleAudioGeneration = async (section, language, contentId, chapterId, voiceId = 'H42MeRDunc17uM624Bpd') => {
  const chapterDocRef = doc(db, 'contentSection', language, section, String(contentId), 'chapters', String(chapterId));

  try {
    console.log('🧪 TEST: Simple audio generation with few words...');

    const { ttsGenerate } = await import('./content/googleFunctions');

    // Test with just 5 words
    const testText = ["Hello world from Neville Goddard"];
    console.log(`🧪 TEST: Generating audio for: "${testText[0]}"`);

    // Mark as generating
    await setDoc(chapterDocRef, {
      audioGenerating: true,
      audioError: false,
      processingStatus: 'TEST: Generating simple audio...'
    }, { merge: true });

    console.log(`🧪 TEST: Using storage paths:`);
    console.log(`  📁 Audio file: contentSection/${language}/${section}/${contentId}/test_${chapterId}_${Date.now()}.mp3`);
    console.log(`  🔗 URL path: contentSection/${language}/${section}/${contentId}/chapters/${chapterId}`);

    // Add timeout wrapper for ElevenLabs processing
    const ttsWithTimeout = async () => {
      return Promise.race([
        ttsGenerate({
          inputText: testText,
          storeAudioPath: `contentSection/${language}/${section}/${contentId}/test_${chapterId}_${Date.now()}.mp3`,
          storeAudioURLPath: `contentSection/${language}/${section}/${contentId}/chapters/${chapterId}`,
          voiceName: voiceId,
          type: 'contentExtra',
          language: language,
          audioStyle: 'read',
          voiceType: 'elevenLabs'
        }),
        new Promise((_, reject) =>
          setTimeout(() => reject(new Error('TTS_TIMEOUT')), 300000) // 5 minutes
        )
      ]);
    };

        let response, audioURL, audioDuration;

    try {
      response = await ttsWithTimeout();
      console.log('🧪 TEST: Raw response from ttsGenerate:', response);
      console.log('🧪 TEST: Response type:', typeof response);
      console.log('🧪 TEST: Response keys:', Object.keys(response || {}));
        } catch (firebaseError) {
      console.log('🧪 TEST: Firebase error caught:', firebaseError.message);
      console.log('🧪 TEST: Full error object:', firebaseError);
      console.log('🧪 TEST: Error keys:', Object.keys(firebaseError));

      // Check if this is the "Response is missing data field" error
      if (firebaseError.message && firebaseError.message.includes('Response is missing data field')) {
        console.log('🧪 TEST: This is the known Firebase internal error, but checking for response data...');

        // Sometimes Firebase errors contain the actual response data
        // Check various places where the response might be hiding
        const possibleResponse = firebaseError.details || firebaseError.data || firebaseError.response || firebaseError.result;

        if (possibleResponse) {
          console.log('🧪 TEST: Found possible response data in error:', possibleResponse);
          response = possibleResponse;
                 } else {
           console.log('🧪 TEST: No response data found in error object');
           console.log('🧪 TEST: Audio likely generated but response lost due to Firebase error');

           // Don't create a mock response - let the function know there was an error
           // The audio might be generated, but we can't reliably get the URL
           response = null;
           console.log('🧪 TEST: Setting response to null to handle manually');
         }
      } else {
        // This is a real error
        throw firebaseError;
      }
    }

    // Try multiple possible response formats
    audioURL = null;
    audioDuration = 0;

    if (response) {
      audioURL = response.fileUrl || response.data?.fileUrl || response.url || response.audioUrl;
      audioDuration = response.duration || response.data?.duration || response.audioDuration || 0;

      console.log('🧪 TEST: Extracted audioURL:', audioURL);
      console.log('🧪 TEST: Extracted duration:', audioDuration);
    } else {
      console.log('🧪 TEST: No response due to Firebase error, but checking if audio was actually generated...');
      // For now, we'll have to rely on manual verification or a different approach
      // The user confirmed the audio was actually generated, so this is a Firebase error handling issue
    }

    if (!audioURL) {
      console.error('🧪 TEST: No audio URL found in response (likely due to Firebase internal error)');
      console.log('🧪 TEST: Audio was likely generated but response lost due to Firebase error');

      // Get expected paths for manual verification
      const { firebaseConfig } = await import('../firebase');
      const projectId = firebaseConfig.projectId;
      const expectedStoragePath = `contentSection/${language}/${section}/${contentId}/test_${chapterId}_*.mp3`;
      const consoleURL = `https://console.firebase.google.com/u/0/project/${projectId}/storage/${projectId}.appspot.com/files/~2FcontentSection%2F${language}%2F${section}%2F${contentId}`;

      console.log('🧪 TEST: Manual verification needed:');
      console.log('🧪 TEST: Check Storage Console:', consoleURL);
      console.log('🧪 TEST: Look for test file pattern:', expectedStoragePath);

      await setDoc(chapterDocRef, {
        audioGenerating: false,
        audioError: false, // Don't mark as error since audio might exist
        audioURL: '',
        audioDuration: 0,
        processingStatus: 'TEST: Manual verification needed - audio likely generated',
        needsManualVerification: true,
        expectedStoragePath,
        consoleURL,
        testNote: 'ElevenLabs likely generated test audio but Firebase response failed'
      }, { merge: true });

      return {
        success: false,
        error: 'Firebase internal error - but audio was likely generated successfully',
        response,
        consoleURL,
        expectedStoragePath,
        note: 'Check Firebase Storage manually - audio might be there despite error'
      };
    }

    // Store successful result
    await setDoc(chapterDocRef, {
      audioURL,
      audioDuration,
      audioGenerating: false,
      audioError: false,
      timeoutOccurred: false,
      audioGeneratedAt: new Date().toISOString(),
      processingMethod: 'test-simple',
      textLength: testText[0].length,
      processingStatus: 'TEST: Success!'
    }, { merge: true });

    console.log('🧪 TEST: SUCCESS! Audio generated:', audioURL);
    return { success: true, audioURL, response };

  } catch (error) {
    console.error('🧪 TEST: Error during simple audio generation:', error);
    console.error('🧪 TEST: Error details:', error.message, error.code);

    await setDoc(chapterDocRef, {
      audioGenerating: false,
      audioError: true,
      audioErrorMessage: `TEST ERROR: ${error.message}`,
      timeoutOccurred: false,
      processingStatus: `TEST: Failed - ${error.message}`
    }, { merge: true });

    return { success: false, error: error.message, errorDetails: error };
  }
};

/**
 * Manually update a chapter's audio URL after verifying it exists in Firebase Storage
 * Use this when ElevenLabs generates audio but Firebase response fails
 *
 * @param {string} section - The content section
 * @param {string} language - The language code
 * @param {string} contentId - The content document ID
 * @param {string} chapterId - The chapter ID
 * @param {string} audioURL - The manually verified audio URL from Firebase Storage
 * @param {number} audioDuration - Optional audio duration if known
 * @returns {boolean} Success status
 */
export const manuallyUpdateChapterAudio = async (section, language, contentId, chapterId, audioURL, audioDuration = 0) => {
  try {
    const chapterDocRef = doc(db, 'contentSection', language, section, String(contentId), 'chapters', String(chapterId));

    await setDoc(chapterDocRef, {
      audioURL,
      audioDuration,
      audioGenerating: false,
      audioError: false,
      needsManualVerification: false,
      processingStatus: '✅ Manually verified and updated',
      manuallyUpdatedAt: new Date().toISOString(),
      audioGeneratedAt: new Date().toISOString()
    }, { merge: true });

    console.log(`✅ Manually updated audio for chapter ${chapterId}: ${audioURL}`);
    return true;

  } catch (error) {
    console.error('Error manually updating chapter audio:', error);
    throw error;
  }
};

/**
 * Get the expected Firebase Storage path for a chapter's audio file
 * Use this to manually check if audio exists in Firebase Storage
 *
 * @param {string} section - The content section
 * @param {string} language - The language code
 * @param {string} contentId - The content document ID
 * @param {string} chapterId - The chapter ID
 * @returns {object} Storage paths and URLs
 */
export const getExpectedAudioPaths = (section, language, contentId, chapterId) => {
  const { firebaseConfig } = require('../firebase');
  const projectId = firebaseConfig.projectId;

  const storagePath = `contentSection/${language}/${section}/${contentId}/chapter_${chapterId}.mp3`;
  const encodedPath = encodeURIComponent(storagePath);
  const expectedURL = `https://firebasestorage.googleapis.com/v0/b/${projectId}.appspot.com/o/${encodedPath}?alt=media`;

  return {
    storagePath,
    encodedPath,
    expectedURL,
    consoleURL: `https://console.firebase.google.com/u/0/project/${projectId}/storage/${projectId}.appspot.com/files/~2F${storagePath.replace(/\//g, '%2F')}`
  };
};

// Make helper functions available in browser console for debugging
if (typeof window !== 'undefined') {
  window.nevilizeHelpers = {
    manuallyUpdateChapterAudio,
    getExpectedAudioPaths,
    testSimpleAudioGeneration
  };
  console.log('🔧 Nevilize helpers available in console: window.nevilizeHelpers');
}
